import React, { useEffect, useState, useRef } from 'react'
import classNames from 'classnames'
import { FileInputProps } from '@toasttab/shared/src/interfaces/Interface.d'

const FileInput = ({
  placeholder,
  disabled = false,
  id,
  name,
  label = 'No file chosen',
  required = false,
  variation = 'default',
  value,
  error,
  onChange,
  onBlur
}: FileInputProps) => {
  const [hasValue, setHasValue] = useState(false)

  const ref = useRef<HTMLInputElement>(null)

  // We need this useEffect call to check the initial input state for the form and apply the correct styling.
  // Sometimes autofill values aren't properly handled on load, which leads to the label overlapping the text.
  // This forces the component to check that value and trigger a rerender of the component to ensures the proper styling.
  useEffect(() => {
    // The !! forces this to a boolean for our state value
    setHasValue(!!value)
  }, [value])

  const labelClassNames = classNames({
    'p-[10px] cursor-pointer flex w-full items-center': true,
    // hidden: !label && variation !== 'default' && variation !== 'inverse',
    'font-sansPro font-normal text-left text-secondary tracking-[0.2px] pointer-events-none mb-[3px]':
      true,
    'text-base leading-[20px]':
      label &&
      (variation === 'inverse' || variation === 'default') &&
      !hasValue,
    'peer-focus/input:text-[10px] peer-focus/input:leading-[10px]':
      label &&
      (variation === 'inverse' || variation === 'default') &&
      !hasValue,
    'text-[10px] leading-[10px] top-[10px]':
      label && (variation === 'inverse' || variation === 'default') && hasValue
  })

  const inputClassNames = classNames(
    'opacity-0 cursor-pointer absolute top-0 left-0 right-0 bottom-0 w-full h-full' // for some reason click event doesn't work on the label so i'm hiding the input ontop of teh label to get a click event
  )

  const errorClassNames = classNames({
    'absolute top-full w-max flex flex-row font-sansPro items-center text-error text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2':
      variation === 'default',
    'absolute top-full w-max flex flex-row font-sansPro items-center text-white text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline-inverse before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2':
      variation === 'inverse'
  })

  //@ts-ignore
  const valueSplit = value ? value.split('\\') : null
  const labelValue = value ? valueSplit[valueSplit.length - 1] : label

  return (
    <>
      <label
        htmlFor={id}
        className={labelClassNames}
        role='button'
        data-testid='FileInput-label'
      >
        <span>
          <svg
            aria-hidden='true'
            xmlns='http://www.w3.org/2000/svg'
            xmlnsXlink='http://www.w3.org/1999/xlink'
            viewBox='0 0 12.77 11.46'
            className='w-4 h-4 mr-2'
          >
            <defs>
              <clipPath id='a'>
                <path
                  d='M-397.06-510.42l-16.56,3.05v17.24a9.77,9.77,0,0,0,2.43,5.88,30.58,30.58,0,0,0,6.05,5.57,59.09,59.09,0,0,0,8.08,4.85,59.09,59.09,0,0,0,8.08-4.85,30.5,30.5,0,0,0,6-5.57,9.82,9.82,0,0,0,2.44-5.88v-17.24Z'
                  fill='none'
                  stroke='#ff4c00'
                  stroke-miterlimit='10'
                />
              </clipPath>
            </defs>
            <path
              d='M-397.06-510.42l-16.56,3.05v17.24a9.77,9.77,0,0,0,2.43,5.88,30.58,30.58,0,0,0,6.05,5.57,59.09,59.09,0,0,0,8.08,4.85,59.09,59.09,0,0,0,8.08-4.85,30.5,30.5,0,0,0,6-5.57,9.82,9.82,0,0,0,2.44-5.88v-17.24Z'
              fill='none'
              stroke='#ff4c00'
              stroke-miterlimit='10'
            />
            <line
              x1='6.34'
              y1='0.71'
              x2='6.26'
              y2='8.04'
              fill='none'
              stroke='#c9cad3'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <polyline
              points='9.29 3.67 6.34 0.65 3.32 3.6'
              fill='none'
              stroke='#c9cad3'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <polyline
              points='11.68 6.13 11.68 10.83 1.12 10.83 1.12 6.13'
              fill='none'
              stroke='#c9cad3'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </span>
        {labelValue}
      </label>
      <input
        ref={ref}
        className={inputClassNames}
        data-testid='FileInput-input'
        disabled={disabled}
        id={id}
        maxLength={255}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        placeholder={!label ? placeholder : null}
        required={required}
        value={value}
        type='file'
      />
      {error && (
        <div
          role='alert'
          className={errorClassNames}
          data-testid='FileInput-error'
        >
          {error}
        </div>
      )}
    </>
  )
}

export default FileInput
